import { LegalShell } from "@/components/legal/LegalShell";

const LAST_UPDATED = "July 19, 2026";

const SECTIONS = [
  {
    id: "why-this-matters",
    title: "Why this page exists",
    body: (
      <>
        <p>
          During the beta phase, <strong>Last Bookmark does not process, hold, or protect
          payments</strong>. If you decide to exchange money with another user for a beta read
          or related service, that arrangement is between the two of you — Last Bookmark isn't
          a party to it.
        </p>
        <p>
          Most people you'll meet here are exactly who they say they are. But whenever money
          moves between strangers online, a small number of bad actors show up too. The
          practices below are the ones we recommend so you can work safely with someone new,
          protect yourself if things go wrong, and keep the community healthy until secure
          platform-managed payments become available.
        </p>
      </>
    ),
  },
  {
    id: "before-you-agree",
    title: "Before you agree on a payment",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Do the work in the open first.</strong> Exchange a message or two, hop on a
            coffee chat, and ask for a short sample of the person's writing or feedback style
            before any money is discussed. Get a feel for who you're working with.
          </li>
          <li>
            <strong>Check their profile.</strong> Do they have a bio, a headline, genres, and a
            history of reviews? Certified Beta Reader status is another useful signal. A
            brand-new empty profile isn't disqualifying, but it's a reason to be extra
            cautious.
          </li>
          <li>
            <strong>Write down what you agreed to.</strong> Deliverables, deadline, price,
            revision expectations, and what happens if either side needs to cancel. A short
            message in your Last Bookmark thread is enough — the point is to have a shared
            record.
          </li>
          <li>
            <strong>Match the payment size to the trust you've built.</strong> First-time
            arrangement? Consider a small paid sample chapter or a split — for example,
            <em>half now, half on delivery</em> — instead of paying the full amount up front.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "safer-payment-methods",
    title: "Safer ways to send payment",
    body: (
      <>
        <p>
          Not all payment methods are equal when it comes to buyer / seller protection. The
          general ranking, safest first:
        </p>
        <ol className="list-decimal pl-6 space-y-2">
          <li>
            <strong>PayPal — Goods &amp; Services</strong> (not Friends &amp; Family).
            "Goods &amp; Services" costs a small fee but gives you a formal dispute process.
            "Friends &amp; Family" is essentially cash — no protection.
          </li>
          <li>
            <strong>A credit card via a legitimate payment link</strong> (Stripe payment link,
            Square invoice, etc.). Credit cards give you chargeback rights with your card
            issuer if the service is not delivered.
          </li>
          <li>
            <strong>An invoicing service</strong> like PayPal Invoicing or Stripe Invoicing.
            You get a paper trail, and both sides know what's expected.
          </li>
        </ol>
        <p>Things to be careful with:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Zelle, Venmo Friends &amp; Family, cash apps, wire transfers, and cryptocurrency</strong> — these are effectively cash and offer little or no recourse if something goes wrong.</li>
          <li><strong>Gift cards</strong> — legitimate service providers never ask for payment in gift cards. If someone does, it's almost certainly a scam.</li>
        </ul>
      </>
    ),
  },
  {
    id: "red-flags",
    title: "Red flags — walk away or check with us",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>They pressure you to move the conversation or payment to a channel where our reporting tools don't work.</li>
          <li>They demand full payment up front for a large project the first time you're working together.</li>
          <li>They ask for payment in gift cards, cryptocurrency, or wire transfers.</li>
          <li>Their profile is brand new, empty, and they can't or won't share a writing / feedback sample.</li>
          <li>They ask you for banking credentials or a copy of your ID for reasons that don't make sense.</li>
          <li>They "overpay" you and ask you to send some back — a classic overpayment scam.</li>
          <li>They rush you or won't put anything in writing.</li>
        </ul>
        <p>
          If any of this happens, stop and email us at{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>
          . We take reports seriously and confidentially.
        </p>
      </>
    ),
  },
  {
    id: "if-something-goes-wrong",
    title: "If something goes wrong",
    body: (
      <>
        <ol className="list-decimal pl-6 space-y-2">
          <li>
            <strong>Contact the other user first.</strong> Miscommunications happen. A calm
            message in your Last Bookmark thread resolves most disputes.
          </li>
          <li>
            <strong>Use the tools of the payment method you chose.</strong> If you paid via
            PayPal Goods &amp; Services, open a dispute in PayPal. If you paid by credit card,
            call your card issuer about a chargeback. Do this within the timeframes they
            require — usually 60–180 days.
          </li>
          <li>
            <strong>Report the user to Last Bookmark.</strong> Even though we can't refund your
            payment during beta, we investigate every report. Repeat offenders are suspended
            or removed from the platform so they can't harm anyone else.
          </li>
          <li>
            <strong>Consider local law enforcement</strong> for larger amounts or clearly
            criminal behaviour (fraud, blackmail, threats). In the U.S., you can also file a
            complaint with the FBI Internet Crime Complaint Center (IC3.gov) or your state
            attorney general.
          </li>
        </ol>
        <p className="text-sm text-muted-foreground">
          A reminder from our{" "}
          <a href="/beta-terms" className="link-underline">Beta Terms</a>: Last Bookmark
          cannot refund, reverse, or otherwise recover payments made directly between users
          during the beta phase.
        </p>
      </>
    ),
  },
  {
    id: "coming-later",
    title: "What's coming later",
    body: (
      <>
        <p>
          Secure platform-managed payments through <strong>Stripe Connect</strong> are planned
          for launch. The exact payment flow, payout timing, protections, fees, and dispute
          process will be clearly explained inside the app before they become available.
        </p>
        <p>
          Until then, the guidance on this page is the safest playbook.
        </p>
      </>
    ),
  },
];

export default function PaymentGuidance() {
  return (
    <LegalShell
      eyebrow="Guide"
      title="Safe payments during beta"
      intro="Practical, plain-English guidance for arranging payment with another Last Bookmark user until secure platform-managed payments become available."
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
      testid="payment-guidance"
    />
  );
}
